// Cave84 — Vintages hooks (Phase 1 Week 3 → updated Week 4)
// TanStack Query wrappers around services/vintages.ts.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult
} from '@tanstack/react-query';
import type { Vintage } from '@cave84/shared-types';
import {
  createVintage,
  findVintageByWineAndYear,
  getVintage,
  listAllVintages,
  listVintagesByWine,
  type VintageCreateInput
} from '@/services/vintages';
import { qk } from '@/services/firestore';

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** All vintages — ~230 docs; cached 10 min. Used by PriceRefreshWizard and Investment dashboard. */
export function useAllVintages(): UseQueryResult<Vintage[]> {
  return useQuery({
    queryKey: qk.vintages.all,
    queryFn: listAllVintages,
    staleTime: 10 * 60 * 1000,
  });
}

export function useVintagesByWine(wineId: string | undefined): UseQueryResult<Vintage[]> {
  return useQuery({
    queryKey: wineId ? qk.vintages.byWine(wineId) : qk.vintages.byWine('__noop__'),
    queryFn: () => (wineId ? listVintagesByWine(wineId) : Promise.resolve([])),
    enabled: !!wineId
  });
}

export function useVintage(id: string | undefined): UseQueryResult<Vintage | null> {
  return useQuery({
    queryKey: id ? qk.vintages.byId(id) : qk.vintages.byId('__noop__'),
    queryFn: () => (id ? getVintage(id) : Promise.resolve(null)),
    enabled: !!id
  });
}

/**
 * Find an existing vintage by wine+year — called in AddBottlePage step 1
 * to detect whether we need to create a new vintage or reuse one.
 * Result is not cached aggressively (staleTime 0) since it's a point-in-time check.
 */
export function useVintageByWineAndYear(
  wineId: string | undefined,
  year: number | undefined
): UseQueryResult<Vintage | null> {
  return useQuery({
    queryKey: ['vintages', { wineId, year }],
    queryFn: () =>
      wineId && year ? findVintageByWineAndYear(wineId, year) : Promise.resolve(null),
    enabled: !!wineId && !!year,
    staleTime: 0
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateVintage(): UseMutationResult<Vintage, Error, VintageCreateInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: VintageCreateInput) => createVintage(input),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: qk.vintages.all });
      void qc.invalidateQueries({ queryKey: qk.vintages.byWine(input.wine_id) });
      void qc.invalidateQueries({ queryKey: qk.wines.all });
    }
  });
}
